import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  Row,
  Col,
  Alert,
  Container,
  FormGroup,
  CardHeader,
} from 'reactstrap';
import { toast } from 'react-toastify';
import Flatpickr from 'react-flatpickr';
import StandardLayout from '@/common/layouts/StandardLayout';
import ConfirmModal from '@/components/ConfirmationModal/ConfirmModal';
import LoadingSpinner from '@/components/LoadingSpinner';
import {
  createMaintenanceWindowRest,
  updateMaintenanceWindowRest,
  getMaintenanceAlertRest,
  deactivateMaintenanceWindowRest,
  createLobbyAlertRest,
  updateLobbyAlertRest,
  getLobbyAlertRest,
  deleteLobbyAlertRest,
} from '@/services/maintenance/maintenance-service';
import { MaintenanceAlertDto, LobbyAlertDto } from '@repo/common-types';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverEvent } from '@dnd-kit/core';
import defaultImage from '@/assets/maintenance-gear.svg';
import LobbyAlert from './components/LobbyAlert/LobbyAlert';

interface MaintenanceWindowFormData {
  title: string;
  description: string;
  endTime: string;
  image?: File | null;
}

interface LobbyAlertFormData {
  text: string;
  endDate: string;
  color: string;
}

const AlertsPage: React.FC = () => {
  const queryClient = useQueryClient();

  const [isDateValid, setIsDateValid] = useState(true);
  const [formData, setFormData] = useState<MaintenanceWindowFormData>({
    title: '',
    description: '',
    endTime: '',
    image: null,
  });
  const [isDragOver, setIsDragOver] = useState(false);
  const [lobbyAlertActive, setLobbyAlertActive] = useState(false);
  const [lobbyAlertData, setLobbyAlertData] = useState<LobbyAlertFormData>({
    text: '',
    endDate: '',
    color: 'green',
  });
  const [lobbyAlertPreview, setLobbyAlertPreview] = useState<LobbyAlertFormData>({
    text: '',
    endDate: '',
    color: 'green',
  });
  const [confirmationModalData, setConfirmationModalData] = useState<{
    title: string;
    message: string;
    confirmButtonText: string;
    cancelButtonText: string;
    disableConfirm: boolean;
    type: 'maintenance' | 'lobby' | 'lobby-deactivate' | null;
  } | null>({
    title: '',
    message: '',
    confirmButtonText: '',
    cancelButtonText: '',
    disableConfirm: false,
    type: null,
  });

  // Query for current maintenance window
  const {
    data: maintenanceWindow,
    isLoading,
    error,
    refetch,
  } = useQuery<MaintenanceAlertDto | null>({
    queryKey: ['maintenanceAlert'],
    queryFn: getMaintenanceAlertRest,
    refetchInterval: 5000, // Refetch every 5 seconds for real-time updates
  });

  const isActive = !!maintenanceWindow;

  // Query for current lobby alert
  const { data: lobbyAlert, isLoading: isLobbyAlertLoading } = useQuery<LobbyAlertDto | null>({
    queryKey: ['lobbyAlert'],
    queryFn: getLobbyAlertRest,
    refetchInterval: 5000, // Refetch every 5 seconds for real-time updates
  });

  // Create/Activate mutation
  const createMutation = useMutation({
    mutationFn: createMaintenanceWindowRest,
    onSuccess: () => {
      toast.success('Maintenance window activated successfully!');
      queryClient.invalidateQueries({ queryKey: ['maintenanceWindow'] });
      setConfirmationModalData(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to activate maintenance window');
      setConfirmationModalData(null);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: updateMaintenanceWindowRest,
    onSuccess: () => {
      toast.success('Maintenance window updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['maintenanceWindow'] });
      setConfirmationModalData(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update maintenance window');
      setConfirmationModalData(null);
    },
  });

  // Deactivate mutation
  const deactivateMutation = useMutation({
    mutationFn: deactivateMaintenanceWindowRest,
    onSuccess: () => {
      toast.success('Maintenance window deactivated successfully!');
      queryClient.invalidateQueries({ queryKey: ['maintenanceWindow'] });
      setConfirmationModalData(null);
      // Reset form when deactivated
      setFormData({
        title: '',
        description: '',
        endTime: '',
        image: null,
      });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to deactivate maintenance window');
      setConfirmationModalData(null);
    },
  });

  // Create/Update lobby alert mutation
  const createLobbyAlertMutation = useMutation({
    mutationFn: createLobbyAlertRest,
    onSuccess: () => {
      toast.success('Lobby alert saved successfully!');
      queryClient.invalidateQueries({ queryKey: ['lobbyAlert'] });
      setLobbyAlertActive(true);
      setConfirmationModalData(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to save lobby alert');
    },
  });

  // Update lobby alert mutation
  const updateLobbyAlertMutation = useMutation({
    mutationFn: updateLobbyAlertRest,
    onSuccess: () => {
      toast.success('Lobby alert updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['lobbyAlert'] });
      setConfirmationModalData(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update lobby alert');
      setConfirmationModalData(null);
    },
  });

  // Delete lobby alert mutation
  const deleteLobbyAlertMutation = useMutation({
    mutationFn: deleteLobbyAlertRest,
    onSuccess: () => {
      toast.success('Lobby alert deactivated successfully!');
      queryClient.invalidateQueries({ queryKey: ['lobbyAlert'] });
      setLobbyAlertActive(false);
      setConfirmationModalData(null);
      // Reset form when deactivated
      setLobbyAlertData({
        text: '',
        endDate: '',
        color: 'green',
      });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to deactivate lobby alert');
      setConfirmationModalData(null);
    },
  });

  // Set form data when maintenance window is loaded
  useEffect(() => {
    if (maintenanceWindow) {
      setFormData({
        title: maintenanceWindow.title || '',
        description: maintenanceWindow.description || '',
        endTime: maintenanceWindow.endTime ? new Date(maintenanceWindow.endTime).toISOString() : '',
        image: null,
      });
    }
  }, [maintenanceWindow]);

  // Set lobby alert data when loaded
  useEffect(() => {
    if (lobbyAlert) {
      setLobbyAlertData({
        text: lobbyAlert.text || '',
        endDate: lobbyAlert.endDate ? new Date(lobbyAlert.endDate).toISOString() : '',
        color: lobbyAlert.color || 'green',
      });
      setLobbyAlertActive(true);
    } else {
      setLobbyAlertActive(false);
    }
  }, [lobbyAlert]);

  const handleToggleActive = () => {
    if (!isActive && !formData.title.trim()) {
      toast.error('Please enter a title before activating the maintenance window');
      return;
    }
    if (!isActive && !isDateValid) {
      toast.error('Please enter a valid future date before activating the maintenance window');
      return;
    }
    setConfirmationModalData({
      title: '⚠️ ALERT!!! This will impact all users on the platform. ⚠️',
      message: isActive
        ? 'Are you sure you want to deactivate the maintenance window?'
        : 'Are you sure you want to activate the maintenance window?',
      confirmButtonText: isActive ? 'Deactivate' : 'Activate',
      cancelButtonText: 'Cancel',
      disableConfirm: false,
      type: 'maintenance',
    });
  };

  const handleConfirmToggle = () => {
    if (confirmationModalData?.type === 'maintenance') {
      if (isActive) {
        // Deactivating
        deactivateMutation.mutate();
      } else {
        // Activating - create new maintenance window
        const alertData: MaintenanceAlertDto = {
          title: formData.title.trim(),
          description: formData.description.trim() || undefined,
          endTime: formData.endTime ? formData.endTime : null,
        };
        createMutation.mutate(alertData);
      }
    } else if (confirmationModalData?.type === 'lobby') {
      const alertData: LobbyAlertDto = {
        text: lobbyAlertData.text.trim(),
        color: lobbyAlertData.color as 'green' | 'orange' | 'red' | 'purple',
        endDate: lobbyAlertData.endDate ?? undefined,
      };

      if (lobbyAlert) {
        // Update existing lobby alert
        updateLobbyAlertMutation.mutate(alertData);
      } else {
        // Create new lobby alert
        createLobbyAlertMutation.mutate(alertData);
      }
    } else if (confirmationModalData?.type === 'lobby-deactivate') {
      // Deactivating lobby alert
      deleteLobbyAlertMutation.mutate();
    }
  };

  const handleInputChange = (field: keyof MaintenanceWindowFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    if (!isDateValid) {
      toast.error('Please enter a valid future date');
      return;
    }

    // Submit does the same thing as activating the toggle
    setConfirmationModalData({
      title: '⚠️ ALERT!!! This will impact all users on the platform. ⚠️',
      message: 'Are you sure you want to save the maintenance window?',
      confirmButtonText: 'Save',
      cancelButtonText: 'Cancel',
      disableConfirm: false,
      type: 'maintenance',
    });
  };

  const handleDateChange = (dates: Date[]) => {
    const selectedDate = new Date(dates[0] ?? new Date()).toISOString();
    handleInputChange('endTime', selectedDate);
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const handleImageUpload = useCallback((files: FileList | File[]) => {
    const file = files[0];
    if (file && file.type.startsWith('image/')) {
      setFormData((prev) => ({ ...prev, image: file }));
    } else {
      toast.error('Please upload a valid image file');
    }
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    setIsDragOver(true);
  }, []);

  const handleDragEnd = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragOver(false);

      const files = Array.from(event.dataTransfer.files);
      handleImageUpload(files);
    },
    [handleImageUpload],
  );

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files) {
        handleImageUpload(files);
      }
    },
    [handleImageUpload],
  );

  const removeImage = useCallback(() => {
    setFormData((prev) => ({ ...prev, image: null }));
  }, []);

  const isFormLoading = createMutation.isPending || updateMutation.isPending || deactivateMutation.isPending;

  const handleLobbyAlertToggle = () => {
    if (!lobbyAlertActive && !lobbyAlertData.text.trim()) {
      toast.error('Please enter text for the lobby alert');
      return;
    }

    if (lobbyAlertActive && lobbyAlert) {
      // Deactivating
      setConfirmationModalData({
        title: '⚠️ Warning ⚠️',
        message: 'Are you sure you want to deactivate the lobby alert?',
        confirmButtonText: 'Deactivate',
        cancelButtonText: 'Cancel',
        disableConfirm: false,
        type: 'lobby-deactivate',
      });
    } else {
      // Activating - create new lobby alert
      setConfirmationModalData({
        title: '⚠️ This will be visible to all users on the platform ⚠️',
        message: 'Are you sure you want to save the lobby alert?',
        confirmButtonText: 'Save',
        cancelButtonText: 'Cancel',
        disableConfirm: false,
        type: 'lobby',
      });
    }
  };

  const handleLobbyAlertChange = (field: keyof LobbyAlertFormData, value: string) => {
    setLobbyAlertData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLobbyAlertDateChange = (dates: Date[]) => {
    const selectedDate = new Date(dates[0] ?? new Date()).toISOString();
    handleLobbyAlertChange('endDate', selectedDate);
  };

  const handleLobbyAlertSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!lobbyAlertData.text.trim()) {
      toast.error('Please enter text for the lobby alert');
      return;
    }

    setConfirmationModalData({
      title: '⚠️ This will be visible to all users on the platform ⚠️',
      message: 'Are you sure you want to save the lobby alert?',
      confirmButtonText: 'Save',
      cancelButtonText: 'Cancel',
      disableConfirm: false,
      type: 'lobby',
    });
  };

  if (isLoading || isLobbyAlertLoading) {
    return (
      <StandardLayout pageTitle="Alerts">
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <LoadingSpinner />
          </div>
        </Container>
      </StandardLayout>
    );
  }

  if (error) {
    return (
      <StandardLayout pageTitle="Alerts">
        <Container fluid>
          <Alert color="danger">
            <h4 className="alert-heading">Error Loading Alert Data</h4>
            <p>Failed to load alert configuration. Please refresh the page.</p>
            <Button onClick={() => refetch()} color="primary">
              Retry
            </Button>
          </Alert>
        </Container>
      </StandardLayout>
    );
  }

  return (
    <StandardLayout pageTitle="Alerts">
      <Container fluid>
        <Row className="justify-content-center">
          <Col className="w-100">
            {isActive && (
              <Alert color="danger" className="mb-4 rounded border-0">
                <strong>🚨 Maintenance Window ACTIVE ⚠️</strong>
              </Alert>
            )}

            {!isActive && !isDateValid && formData.endTime && (
              <Alert color="warning" className="mb-4 rounded border-0">
                <strong>⚠️ Date must be in the future</strong>
              </Alert>
            )}

            {/* Platform Alert Card */}
            <Card className="rounded-3 mb-4 border-0 shadow-lg">
              <CardHeader className="bg-dark border-0">
                <h4 className="mb-0">Platform Alert</h4>
              </CardHeader>
              <CardBody className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className="d-flex align-items-center justify-content-between">
                    <FormGroup switch>
                      <Input
                        type="switch"
                        role="switch"
                        checked={isActive}
                        onChange={handleToggleActive}
                        disabled={!isActive && (!formData.title.trim() || !isDateValid)}
                      />
                      <Label check>Active</Label>
                    </FormGroup>
                  </div>
                </div>

                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <div className="mb-4">
                        <Label htmlFor="title" className="form-label mb-2 text-white">
                          Title <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          id="title"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          required
                          disabled={isActive}
                          maxLength={200}
                          placeholder="Enter maintenance title"
                          className="text-white"
                        />
                      </div>

                      <div className="mb-4">
                        <Label htmlFor="description" className="form-label mb-2 text-white">
                          Description
                        </Label>
                        <Input
                          type="textarea"
                          id="description"
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          disabled={isActive}
                          maxLength={500}
                          rows={3}
                          placeholder="Enter maintenance description"
                        />
                      </div>

                      <div className="mb-4">
                        <Label htmlFor="endTime" className="form-label mb-2 text-white">
                          End Time
                        </Label>
                        <Flatpickr
                          className="form-control"
                          disabled={isActive}
                          options={{
                            enableTime: true,
                            dateFormat: 'Y-m-d H:i',
                            time_24hr: true,
                            allowInvalidPreload: true,
                            minDate: new Date(),
                            defaultDate: new Date(),
                            minuteIncrement: 1,
                          }}
                          value={formData.endTime || undefined}
                          onChange={handleDateChange}
                          placeholder="Select date and time"
                        />
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="mb-4">
                        <Label className="form-label mb-2 text-white">Upload Image (Coming Soon)</Label>
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragOver={handleDragOver}
                          onDragEnd={handleDragEnd}
                        >
                          <div
                            className={`upload-area d-flex flex-column align-items-center justify-content-center ${
                              isDragOver ? 'drag-over' : ''
                            }`}
                            style={{
                              border: `2px dashed ${isDragOver ? '#7c3aed' : '#6b7280'}`,
                              borderRadius: '8px',
                              padding: '40px 20px',
                              minHeight: '200px',
                              transition: 'all 0.2s ease',
                            }}
                            onDrop={handleDrop}
                            onDragOver={(e) => e.preventDefault()}
                            onDragEnter={(e) => e.preventDefault()}
                            onClick={() => {
                              // document.getElementById('file-input')?.click()
                            }}
                          >
                            <div className="text-center">
                              <div className="mb-3">
                                <img
                                  src={defaultImage}
                                  alt="Preview"
                                  style={{
                                    maxWidth: '120px',
                                    maxHeight: '120px',
                                    borderRadius: '8px',
                                    objectFit: 'cover',
                                  }}
                                />
                              </div>
                              <p className="mb-2 text-white">{formData.image?.name ?? 'maintenance-gear.svg'}</p>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeImage();
                                }}
                                style={{ borderRadius: '6px' }}
                                disabled
                              >
                                Remove
                              </Button>
                            </div>
                          </div>
                          <input
                            id="file-input"
                            type="file"
                            accept="image/*"
                            onChange={handleFileInputChange}
                            style={{ display: 'none' }}
                            disabled={isActive}
                          />
                        </DndContext>
                      </div>
                    </Col>
                  </Row>

                  {!isActive && (
                    <div className="mt-4">
                      <Button
                        color="primary"
                        type="submit"
                        disabled={isActive || !formData.title.trim() || !isDateValid || isFormLoading}
                      >
                        {isFormLoading ? <LoadingSpinner /> : 'Save'}
                      </Button>
                    </div>
                  )}
                </Form>
              </CardBody>
            </Card>

            {/* Lobby Alert Card */}
            <Card className="rounded-3 border-0 shadow-lg">
              <CardHeader className="bg-dark border-0">
                <h4 className="mb-0">Lobby Alert</h4>
              </CardHeader>
              <CardBody className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className="d-flex align-items-center">
                    <FormGroup switch>
                      <Input
                        type="switch"
                        role="switch"
                        checked={lobbyAlertActive}
                        onChange={handleLobbyAlertToggle}
                        disabled={
                          (!lobbyAlertData.text.trim() && !lobbyAlertActive) ||
                          createLobbyAlertMutation.isPending ||
                          updateLobbyAlertMutation.isPending ||
                          deleteLobbyAlertMutation.isPending
                        }
                      />
                      <Label check>Active</Label>
                    </FormGroup>
                  </div>
                </div>

                <Form onSubmit={handleLobbyAlertSubmit}>
                  <Row>
                    <Col md={6}>
                      <div className="mb-4">
                        <Label htmlFor="lobbyText" className="form-label mb-2 text-white">
                          Text <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          id="lobbyText"
                          value={lobbyAlertData.text}
                          onChange={(e) => handleLobbyAlertChange('text', e.target.value)}
                          required
                          placeholder="Enter alert text"
                          className="text-white"
                        />
                      </div>

                      <div className="mb-4">
                        <Label htmlFor="lobbyEndDate" className="form-label mb-2 text-white">
                          End Date
                        </Label>
                        <Flatpickr
                          className="form-control"
                          options={{
                            enableTime: true,
                            dateFormat: 'Y-m-d H:i',
                            time_24hr: true,
                            allowInvalidPreload: true,
                            minDate: new Date(),
                            defaultDate: new Date(),
                            minuteIncrement: 1,
                          }}
                          value={lobbyAlertData.endDate || undefined}
                          onChange={handleLobbyAlertDateChange}
                          placeholder="Select date and time"
                        />
                      </div>

                      <div className="mb-4">
                        <Label htmlFor="lobbyColor" className="form-label mb-2 text-white">
                          Color
                        </Label>
                        <Input
                          type="select"
                          id="lobbyColor"
                          value={lobbyAlertData.color}
                          onChange={(e) => handleLobbyAlertChange('color', e.target.value)}
                          className="text-white"
                        >
                          <option value="green">Green</option>
                          <option value="orange">Orange</option>
                          <option value="red">Red</option>
                          <option value="purple">Purple</option>
                        </Input>
                      </div>
                    </Col>
                    <Col md={6} className="d-flex justify-content-center align-items-center">
                      <LobbyAlert
                        text={lobbyAlertPreview.text}
                        type={lobbyAlertPreview.color}
                        endDate={lobbyAlertPreview.endDate}
                      />
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-start align-items-center mt-4 gap-2">
                    <Button
                      color="none"
                      onClick={() => {
                        setLobbyAlertData({
                          text: '',
                          endDate: '',
                          color: 'green',
                        });
                        setLobbyAlertPreview({
                          text: '',
                          endDate: '',
                          color: 'green',
                        });
                      }}
                      disabled={
                        createLobbyAlertMutation.isPending ||
                        updateLobbyAlertMutation.isPending ||
                        deleteLobbyAlertMutation.isPending
                      }
                    >
                      Clear
                    </Button>
                    <Button
                      disabled={!lobbyAlertData.text.trim()}
                      onClick={() => {
                        setLobbyAlertPreview({
                          text: lobbyAlertData.text,
                          endDate: lobbyAlertData.endDate,
                          color: lobbyAlertData.color,
                        });
                      }}
                    >
                      Preview
                    </Button>
                    <Button
                      color="primary"
                      type="submit"
                      disabled={
                        !lobbyAlertData.text.trim() ||
                        createLobbyAlertMutation.isPending ||
                        updateLobbyAlertMutation.isPending ||
                        deleteLobbyAlertMutation.isPending
                      }
                    >
                      {lobbyAlertActive ? (
                        updateLobbyAlertMutation.isPending ? (
                          <LoadingSpinner />
                        ) : (
                          'Update'
                        )
                      ) : createLobbyAlertMutation.isPending ? (
                        <LoadingSpinner />
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {confirmationModalData?.type && (
          <ConfirmModal
            title={confirmationModalData?.title ?? ''}
            message={confirmationModalData?.message ?? ''}
            onConfirm={handleConfirmToggle}
            onClose={() => setConfirmationModalData(null)}
            confirmButtonText={confirmationModalData?.confirmButtonText ?? ''}
            cancelButtonText={confirmationModalData?.cancelButtonText ?? ''}
            disableConfirm={confirmationModalData?.disableConfirm ?? false}
          />
        )}
      </Container>
    </StandardLayout>
  );
};

export default AlertsPage;
